import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Stack, Divider,
  InputAdornment, CircularProgress,
} from '@mui/material';
import { Close, LockOutlined, CheckCircle, Warning, InfoOutlined } from '@mui/icons-material';
import { useCloseSessionMutation, useLazyGetSessionQuery } from '../../services/cashSessionApi';
import { useToast } from '../common/ToastProvider';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F8FAFC',
    borderRadius: '8px',
    fontSize: '0.875rem',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#CBD5E1' },
    '&.Mui-focused fieldset': { borderColor: '#4F46E5' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4F46E5' },
};

const fmt = (val) =>
  `₹${parseFloat(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SummaryRow = ({ label, value, bold, color, negate }) => {
  const num = parseFloat(value || 0);
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.73rem' }, color: color || '#64748B', fontWeight: bold ? 700 : 400 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.73rem' }, color: color || '#1E293B', fontWeight: bold ? 700 : 500 }}>
        {negate ? fmt(-num) : fmt(num)}
      </Typography>
    </Box>
  );
};

const CloseSessionModal = ({ open, onClose, session, onSessionClosed }) => {
  const showToast = useToast();

  const [closingBalance, setClosingBalance] = useState('');
  const [balanceError, setBalanceError]     = useState('');
  const [liveSummary, setLiveSummary]       = useState(null);

  const [closeSession,      { isLoading: isClosing  }] = useCloseSessionMutation();
  const [triggerGetSession, { isLoading: isFetching }] = useLazyGetSessionQuery();

  // Fetch latest summary each time modal opens
  useEffect(() => {
    if (open && session?.id) {
      setClosingBalance('');
      setBalanceError('');
      setLiveSummary(null);

      triggerGetSession(session.id)
        .unwrap()
        .then((res) => { if (res?.data?.session) setLiveSummary(res.data.session); })
        .catch(() => showToast('Could not load session summary. Please try again.', 'error'));
    }
  }, [open, session?.id]); // eslint-disable-line

  const expectedCash = useMemo(() => {
    const val = parseFloat(liveSummary?.summary?.expected_cash ?? session?.summary?.expected_cash ?? 0);
    return isNaN(val) ? 0 : val;
  }, [liveSummary, session]);

  const diffResult = useMemo(() => {
    const closing = parseFloat(closingBalance);
    if (closingBalance === '' || isNaN(closing)) return null;
    const diff = closing - expectedCash;
    if (diff > 0)  return { label: `OVER BY ${fmt(diff)}`,   icon: 'check', color: '#065F46', bg: '#D1FAE5' };
    if (diff < 0)  return { label: `SHORT BY ${fmt(-diff)}`, icon: 'warn',  color: '#7F1D1D', bg: '#FEE2E2' };
    return             { label: 'BALANCED',                   icon: 'check', color: '#065F46', bg: '#D1FAE5' };
  }, [closingBalance, expectedCash]);

  const handleBalanceChange = (e) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setClosingBalance(val);
      if (balanceError) setBalanceError('');
    }
  };

  const validate = () => {
    const val = parseFloat(closingBalance);
    if (closingBalance === '' || isNaN(val)) { setBalanceError('Closing balance is required'); return false; }
    if (val < 0) { setBalanceError('Closing balance cannot be negative'); return false; }
    setBalanceError('');
    return true;
  };

  const handleConfirm = async () => {
    if (!validate()) return;
    try {
      const res = await closeSession({ id: session.id, closing_balance: parseFloat(closingBalance) }).unwrap();
      onSessionClosed(res.data.session);
      showToast('Session closed successfully', 'success');
    } catch (err) {
      showToast(err?.data?.error || 'Failed to close session. Please try again.', 'error');
    }
  };

  const displaySession = liveSummary || session;
  const summary        = displaySession?.summary || {};

  return (
    <Dialog
      open={open}
      onClose={isClosing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '12px' },
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: 'auto' },
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.5, sm: 2 },
        display: 'flex', alignItems: 'center', gap: 1.25,
        borderBottom: '1px solid #F1F5F9',
      }}>
        <Box sx={{
          width: { xs: 28, sm: 34 }, height: { xs: 28, sm: 34 },
          borderRadius: '8px', bgcolor: '#FEF2F2',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <LockOutlined sx={{ fontSize: { xs: 16, sm: 19 }, color: '#EF4444' }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize={{ xs: '0.9rem', sm: '1rem' }} color="#1E293B" lineHeight={1.3}>
            Close Cash Session
          </Typography>
          {displaySession?.session_number && (
            <Typography fontSize={{ xs: '0.65rem', sm: '0.7rem' }} color="#94A3B8" fontWeight={500} noWrap>
              {displaySession.session_number}
            </Typography>
          )}
        </Box>
        <Button
          size="small" onClick={onClose} disabled={isClosing}
          sx={{ minWidth: 0, p: 0.5, color: '#94A3B8', flexShrink: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
        >
          <Close sx={{ fontSize: 18 }} />
        </Button>
      </DialogTitle>

      {/* Scrollable content — constrained height on mobile */}
      <DialogContent sx={{
        px: { xs: 2, sm: 2.5 },
        pt: { xs: 1.5, sm: 2 },
        pb: 1,
        maxHeight: { xs: '55vh', sm: 'none' },
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '10px' },
      }}>

        <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05em', mb: 0.75 }}>
          SESSION SUMMARY
        </Typography>

        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} sx={{ color: '#4F46E5' }} />
          </Box>
        ) : (
          <Box sx={{
            bgcolor: '#F8FAFC', border: '1px solid #F1F5F9',
            borderRadius: '8px',
            px: { xs: 1.25, sm: 1.75 },
            py: { xs: 0.75, sm: 1.25 },
            mb: { xs: 1.5, sm: 2 },
          }}>
            <SummaryRow label="Opening Balance"  value={displaySession?.opening_balance} />
            <SummaryRow label="Total Cash Sales" value={summary.total_cash_sales} />
            <SummaryRow label="Change Given"     value={summary.total_change_given}  negate />
            <SummaryRow label="Cash Refunds"     value={summary.total_cash_refunds}  negate />
            {parseFloat(summary.total_voucher_sales_cash || 0) > 0 && <SummaryRow label="Voucher Sales (Cash)" value={summary.total_voucher_sales_cash} />}
            {parseFloat(summary.total_voucher_sale_refunds_cash || 0) > 0 && <SummaryRow label="Voucher Refunds (Cash)" value={summary.total_voucher_sale_refunds_cash} negate />}
            <SummaryRow label="Cash Ins"         value={summary.total_cash_ins} />
            <SummaryRow label="Cash Outs"        value={summary.total_cash_outs}     negate />
            <Divider sx={{ my: 0.6 }} />
            <SummaryRow
              label="Expected Cash"
              value={summary.expected_cash ?? displaySession?.opening_balance}
              bold color="#1E293B"
            />
          </Box>
        )}

        <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

        {/* Closing balance */}
        <TextField
          fullWidth
          label="Physical Cash Count"
          required
          value={closingBalance}
          onChange={handleBalanceChange}
          disabled={isClosing || isFetching}
          error={!!balanceError}
          helperText={balanceError || ' '}
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.9rem' }}>₹</Typography>
              </InputAdornment>
            ),
          }}
          sx={{ ...fieldSx, mb: 0.5 }}
        />

        {/* Live difference */}
        {diffResult && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: diffResult.bg, borderRadius: '8px',
            px: 1.5, py: { xs: 0.6, sm: 1 }, mb: 1,
          }}>
            {diffResult.icon === 'check'
              ? <CheckCircle sx={{ fontSize: 16, color: diffResult.color }} />
              : <Warning      sx={{ fontSize: 16, color: diffResult.color }} />}
            <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 800, color: diffResult.color }}>
              {diffResult.label}
            </Typography>
          </Box>
        )}

        {/* Warning note */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1,
          bgcolor: '#FFFBEB', border: '1px solid #FEF3C7',
          borderRadius: '8px',
          px: { xs: 1.25, sm: 1.5 },
          py: { xs: 0.6, sm: 1 },
          mb: 0.5,
        }}>
          <InfoOutlined sx={{ fontSize: 14, color: '#92400E', mt: 0.15, flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.72rem' }, color: '#92400E', lineHeight: 1.5 }}>
            Count carefully. Once confirmed, corrections can only be made by your store manager.
          </Typography>
        </Box>

      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 },
        pt: { xs: 1, sm: 1.5 },
        pb: { xs: 1.5, sm: 2.5 },
        gap: 1,
      }}>
        <Button
          variant="outlined" onClick={onClose} disabled={isClosing}
          sx={{
            flex: 1, py: { xs: 0.9, sm: 1.1 }, fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            borderRadius: '8px', textTransform: 'none',
            borderColor: '#E2E8F0', color: '#64748B',
            '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleConfirm}
          disabled={isClosing || isFetching || closingBalance === ''}
          disableElevation
          sx={{
            flex: 2, py: { xs: 0.9, sm: 1.1 }, fontWeight: 700,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            borderRadius: '8px', textTransform: 'none',
            bgcolor: '#EF4444', color: '#fff',
            '&:hover': { bgcolor: '#DC2626' },
            '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
          }}
        >
          {isClosing ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} sx={{ color: '#fff' }} />
              <span>Closing...</span>
            </Stack>
          ) : (
            'Confirm & Close'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseSessionModal;
